import React, { memo, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { DragList } from './DragList';
import { QuickActions } from './QuickActions';
import {
  IQuickDragListProps,
  IQuickDragItem,
  ISortableEndEvent,
  ISortableEvent,
  TQuickAction,
} from './types';
import './index.scss';

/**
 * 快速拖拽列表组件
 * 支持拖拽排序、快速操作（置顶、上移、下移、置底）
 */
const QuickDragListInner: React.FC<IQuickDragListProps> = ({
  value,
  onChange,
  title,
  height,
  maxHeight,
  className,
  style,
  renderItem,
  chosenClass,
  dragClass,
  ghostClass,
  fallbackClass,
  activeClass,
  onChoose,
  onStart,
  onEnd,
  onUpdate,
  onAdd,
  onRemove,
  onFilter,
  onMove,
  animation,
  delay,
  handle,
  filter,
  disabled,
}) => {
  // 内部管理的选中索引
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const pendingBoundaryScrollRef = useRef<TQuickAction | undefined>(undefined);

  // 处理列表项点击
  const handleItemClick = useCallback((index: number): void => {
    setSelectedIndex(index);
  }, []);

  // 处理拖拽结束
  const handleDragEnd = useCallback((newItems: IQuickDragItem[]): void => {
    onChange(newItems);
  }, [onChange]);

  const handleScrollToBoundary = useCallback((action: TQuickAction): void => {
    const container = wrapperRef.current?.querySelector('.dbl-quick-drag-list-container');
    if (!container) {
      return;
    }
    if (action === 'pinned') {
      container.scrollTo({ top: 0, behavior: 'auto' });
      return;
    }
    if (action === 'trailing') {
      container.scrollTo({ top: container.scrollHeight, behavior: 'auto' });
    }
  }, []);

  useLayoutEffect(() => {
    const pendingAction = pendingBoundaryScrollRef.current;
    if (!pendingAction) {
      return;
    }
    requestAnimationFrame(() => {
      handleScrollToBoundary(pendingAction);
      pendingBoundaryScrollRef.current = undefined;
    });
  }, [value, handleScrollToBoundary]);

  // 处理快速操作
  const handleQuickMove = useCallback((action: TQuickAction): void => {
    if (selectedIndex === undefined || value.length <= 1) {
      return;
    }

    const items = Array.from(value);
    let targetIndex: number;

    switch (action) {
      case 'pinned':
        targetIndex = 0;
        break;
      case 'advance':
        targetIndex = selectedIndex - 1;
        break;
      case 'future':
        targetIndex = selectedIndex + 1;
        break;
      case 'trailing':
        targetIndex = items.length - 1;
        break;
      default:
        return;
    }

    // 执行移动
    const [movedItem] = items.splice(selectedIndex, 1);
    items.splice(targetIndex, 0, movedItem);

    // 更新选中索引
    setSelectedIndex(targetIndex);

    // 通知外部数据变更
    if (action === 'pinned' || action === 'trailing') {
      pendingBoundaryScrollRef.current = action;
    }
    onChange(items);
  }, [selectedIndex, value, onChange, handleScrollToBoundary]);

  // 处理拖拽选中事件（更新内部选中状态）
  const handleChoose = useCallback((evt: ISortableEvent): void => {
    if (evt.oldIndex !== undefined) {
      setSelectedIndex(evt.oldIndex);
    }
    onChoose?.(evt);
  }, [onChoose]);

  // 处理拖拽结束事件（更新内部选中状态）
  const handleEnd = useCallback((evt: ISortableEndEvent): void => {
    if (evt.newIndex !== undefined) {
      setSelectedIndex(evt.newIndex);
    }
    onEnd?.(evt);
  }, [onEnd]);

  return (
    <div className="dbl-quick-drag-list-wrapper" ref={wrapperRef}>
      <DragList
        items={value}
        selectedIndex={selectedIndex}
        onItemClick={handleItemClick}
        onDragEnd={handleDragEnd}
        height={height}
        maxHeight={maxHeight}
        className={className}
        style={style}
        renderItem={renderItem}
        title={title}
        activeClass={activeClass}
        chosenClass={chosenClass}
        dragClass={dragClass}
        ghostClass={ghostClass}
        fallbackClass={fallbackClass}
        onChoose={handleChoose}
        onStart={onStart}
        onEnd={handleEnd}
        onUpdate={onUpdate}
        onAdd={onAdd}
        onRemove={onRemove}
        onFilter={onFilter}
        onMove={onMove}
        animation={animation}
        delay={delay}
        handle={handle}
        filter={filter}
        disabled={disabled}
      />
      <QuickActions
        selectedIndex={selectedIndex}
        itemsLength={value.length}
        onMove={handleQuickMove}
      />
    </div>
  );
};

// 通过 memo 阻断父层无关状态更新的传导；仅在 props 实际变更时重新渲染。
export const QuickDragList = memo(QuickDragListInner);
QuickDragList.displayName = 'QuickDragList';

export default QuickDragList;

